import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "../DashboardHeader/DashboardHeader";
import StepHeader from "../StepHeader/StepHeader";
import ActionBar from "../ActionBar/ActionBar";
import { NotificationContainer, NotificationManager } from "react-notifications";
import { APP_STEPS } from "../config/steps";
import "./Documents.css";
import WhatsAppWidget from "../WhatsAppWidget/WhatsAppWidget";

const Resume = () => {
  // ---- state ----
  const [files, setFiles] = useState({});
  const [resumeData, setResumeData] = useState({});
  const [resumeId, setResumeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const applicationId = parseInt(localStorage.getItem("id"), 10);
  const status = localStorage.getItem("application_status");
  const isDisabled = status !== "incomplete";
  const currentStep = 3; // index in APP_STEPS

  const requiredFields = [
    "attested_applicant_cnic_front",
    "attested_applicant_cnic_back",
    "student_id_card",
    "attested_father_guardian_cnic_front",
    "attested_father_guardian_cnic_back",
    "paid_fee_challan",
    "electricity_bill",
  ];

  // Human-readable labels
  const fieldLabels = {
    attested_applicant_cnic_front: "Your CNIC / B-Form — front",
    attested_applicant_cnic_back: "Your CNIC / B-Form — back",
    student_id_card: "Student ID card/Bonafide certificate",
    attested_father_guardian_cnic_front: "Father / Guardian CNIC — front",
    attested_father_guardian_cnic_back: "Father / Guardian CNIC — back",
    paid_fee_challan: "Paid fee challan",
    electricity_bill: "Electricity bill",
  };

  // ---- fetch existing documents ----
  useEffect(() => {
    const fetchResumeData = async () => {
      try {
        const campaignRes = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/campaigns?sort=createdAt:desc&pagination[limit]=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const campaignData = await campaignRes.json();
        const latestCampaign = campaignData.data[0];
        if (!latestCampaign) return;

        const resumeRes = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/documents-uploads?&filters[application][id][$eq]=${applicationId}&populate=*`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const resumeResult = await resumeRes.json();
        const resume = resumeResult.data?.[0];

        if (resume) {
          setResumeId(resume.id);
          const existingFiles = {};
          requiredFields.forEach((field) => {
            if (resume[field]?.id) {
              existingFiles[field] = {
                file: null,
                path: resume[field]?.url || "",
                name: resume[field].name,
              };
            }
          });
          setResumeData(existingFiles);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching resume data:", error);
        setLoading(false);
      }
    };
    fetchResumeData();
  }, [applicationId, token]);

  // ---- file change ----
  const handleFileChange = (e, field) => {
    if (e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      if (selectedFile.size <= 5000000) {
        setResumeData((prev) => ({
          ...prev,
          [field]: { ...prev[field], file: selectedFile, name: selectedFile.name },
        }));
      } else {
        NotificationManager.error("File must be less than 5MB");
        e.target.value = "";
      }
    }
  };

  // ---- remove file (works for both new + server-uploaded) ----
  const handleRemove = (field) => {
    setResumeData((prev) => ({
      ...prev,
      [field]: { file: null, path: "", name: "" },
    }));
  };

  // ---- mark a server-uploaded file as "replaced" so user can pick a new one ----
  const handleReplace = (field) => {
    setResumeData((prev) => ({
      ...prev,
      [field]: { file: null, path: "", name: "" },
    }));
    // focus the hidden input after clearing
    setTimeout(() => {
      const input = document.getElementById(`doc_${field}`);
      if (input) input.value = "";
    }, 0);
  };

  // ---- count uploaded ----
  const uploadedCount = requiredFields.filter(
    (field) => resumeData[field]?.file || resumeData[field]?.path
  ).length;
  const totalRequired = requiredFields.length;
  const progressPct = Math.round((uploadedCount / totalRequired) * 100);

  // ---- submit (existing API logic kept) ----
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (isDisabled) return;
    if (status === "new") { navigate("/application/personal-statement"); return; }
    setErrorMessage("");

    // Check missing files
    const missingFiles = requiredFields.filter(
      (field) => !resumeData[field]?.file && !resumeData[field]?.path
    );
    if (missingFiles.length > 0) {
      setErrorMessage(`Please upload: ${missingFiles.map((f) => fieldLabels[f]).join(", ")}`);
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      for (const field of requiredFields) {
        const fileData = resumeData[field];
        if (fileData?.file) {
          formData.append(field, fileData.file);
        }
      }
      formData.append("application_id", applicationId);

      let response;
      if (resumeId) {
        response = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/documents-uploads/${resumeId}`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          }
        );
      } else {
        response = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/documents-uploads`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          }
        );
      }

      if (!response.ok) throw new Error("Failed to upload documents");

      if (!resumeId) {
        const createdData = await response.json();
        localStorage.setItem("require_document", createdData.id);
      }

      setSaving(false);
      NotificationManager.success("Documents uploaded successfully!");
      navigate("/application/personal-statement");
    } catch (error) {
      console.error("Error uploading documents:", error);
      setSaving(false);
      NotificationManager.error("Error uploading files. Please try again.");
    }
  };

  // SVG upload icon
  const UP_ICON = (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 16V4M7 9l5-5 5-5" />
      <path d="M4 16v4h16v-4" />
    </svg>
  );

  return (
    <div>
      {/* Top bar */}
      <DashboardHeader />

      {/* Step progress */}
      <div style={{ background: "rgba(255,255,255,.97)", borderBottom: "1px solid #dbe3e1" }}>
        <div className="doc" style={{ paddingBottom: 0 }}>
          <StepHeader steps={APP_STEPS} current={currentStep} />
        </div>
      </div>

      {/* Content */}
      <div className="doc">
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
            <div className="spinner-bubble spinner-bubble-primary"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* page heading */}
            <h2 className="doc__title">Upload documents</h2>
            <p className="doc__sub">JPG, PNG or PDF, up to 5 MB each. Make sure the text is readable.</p>
            <p className="doc__req-note">Fields marked <em style={{ color: "#a9443b" }}>*</em> are required.</p>

            {/* Progress indicator */}
            <div className="doc__progress" aria-live="polite">
              <span><b>{uploadedCount} of {totalRequired}</b> required documents uploaded</span>
              <div className="doc__progress-track">
                <span style={{ width: `${progressPct}%` }} />
              </div>
            </div>

            {/* Tip note */}
            <div className="doc__note">
              📷 Tip: on your phone, tap the upload area and choose <b>Camera</b> to take a photo directly.
            </div>

            {/* Required documents */}
            <div className="doc__card">
              <h3>Required Documents</h3>

              {requiredFields.map((field) => {
                const fileInfo = resumeData[field];
                const hasFile = fileInfo?.file || fileInfo?.path;

                return (
                  <div className="doc__field" key={field}>
                    <label className="doc__label">{fieldLabels[field]} <em style={{ color: "#a9443b" }}>*</em></label>

                    <div className={`doc__file${hasFile ? " has" : ""}`}>
                      <input
                        type="file"
                        id={`doc_${field}`}
                        accept=".pdf,.png,.jpeg,.jpg"
                        onChange={(e) => handleFileChange(e, field)}
                        hidden
                        disabled={isDisabled}
                      />

                      {hasFile ? (
                        <div className="doc__got">
                          <span className="doc__ok">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                              <path d="M5 12l5 5 9-10" />
                            </svg>
                          </span>
                          <span className="doc__meta">
                            <span className="doc__fname">
                              {fileInfo?.file ? fileInfo.file.name : fileInfo?.name || "document"}
                            </span>
                            {fileInfo?.file && <small>{(fileInfo.file.size / 1024 / 1024).toFixed(2)} MB</small>}
                          </span>
                          <span className="doc__file-actions">
                            {/* Replace: clears current so user can pick a new file */}
                            <button
                              type="button"
                              className="doc__replace"
                              onClick={() => handleReplace(field)}
                              aria-label="Replace file"
                              disabled={isDisabled}
                            >
                              Replace
                            </button>
                            {/* Delete: removes file completely */}
                            <button type="button" className="doc__x" onClick={() => handleRemove(field)} aria-label="Remove file" disabled={isDisabled}>✕</button>
                          </span>
                        </div>
                      ) : (
                        <label htmlFor={`doc_${field}`} className="doc__pick">
                          {UP_ICON}
                          <span>
                            <b>Tap to upload</b>
                            <small>JPG, PNG or PDF · max 5 MB</small>
                          </span>
                        </label>
                      )}
                    </div>

                    {/* View uploaded file */}
                    {fileInfo?.path && !fileInfo?.file && (
                      <button
                        type="button"
                        onClick={() => window.open(fileInfo.path, "_blank")}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: "6px",
                          background: "none", border: "1px solid #dfe6e4", borderRadius: "10px",
                          padding: "6px 12px", fontWeight: 600, fontSize: "12px",
                          color: "#115e59", cursor: "pointer", marginTop: "6px",
                        }}
                      >
                        View Uploaded
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Error message */}
              {errorMessage && <p className="doc__err" style={{ margin: "10px 0 0" }}>{errorMessage}</p>}
            </div>
          </form>
        )}
      </div>

      {/* Bottom action bar */}
      <ActionBar
        onBack={() => navigate("/application/finance-details")}
        onNext={handleSubmit}
        backLabel="← Back"
        nextLabel="Continue →"
        nextDisabled={isDisabled}
        nextLoading={saving}
      />

      <NotificationContainer />
      <WhatsAppWidget />
    </div>
  );
};

export default Resume;
